export const API_BASE = (import.meta.env.VITE_API_BASE as string) ?? 'http://localhost:3000';

let tokenScope = 'aiagg';

/** Separate apps, one origin. */
export function setTokenScope(scope: string) {
  tokenScope = scope;
}

const accessKey = () => `${tokenScope}.access_token`;
const refreshKey = () => `${tokenScope}.refresh_token`;

interface ApiErrorBody {
  error: {
    type: string;
    code?: string;
    message: string;
    request_id?: string;
    details?: Record<string, unknown>;
  };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly type: string,
    message: string,
    readonly requestId?: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const tokens = {
  get access(): string | null {
    return localStorage.getItem(accessKey());
  },
  get refresh(): string | null {
    return localStorage.getItem(refreshKey());
  },
  set(access: string, refresh: string) {
    localStorage.setItem(accessKey(), access);
    localStorage.setItem(refreshKey(), refresh);
  },
  clear() {
    localStorage.removeItem(accessKey());
    localStorage.removeItem(refreshKey());
  },
};

let refreshInFlight: Promise<boolean> | null = null;

/** Single-flight; refresh tokens rotate. */
async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const refresh = tokens.refresh;
    if (!refresh) return false;
    try {
      const response = await fetch(`${API_BASE}/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (!response.ok) return false;
      const data = await response.json();
      tokens.set(data.access_token, data.refresh_token);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Customer key, not JWT. */
  apiKey?: string;
  signal?: AbortSignal;
}

export async function api<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (options.apiKey) headers.authorization = `Bearer ${options.apiKey}`;
    else if (tokens.access) headers.authorization = `Bearer ${tokens.access}`;

    return fetch(`${API_BASE}${path}`, {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  };

  let response = await send();

  // retry once after refresh
  if (response.status === 401 && !options.apiKey && tokens.refresh) {
    if (await refreshAccessToken()) response = await send();
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const data = text ? safeParse(text) : null;

  if (!response.ok) {
    const body = data as ApiErrorBody | null;
    if (response.status === 401 && !options.apiKey) {
      tokens.clear();
      window.dispatchEvent(new CustomEvent('aiagg:unauthenticated'));
    }
    throw new ApiError(
      response.status,
      body?.error?.code ?? body?.error?.type ?? 'request_error',
      body?.error?.message ?? `Request failed with status ${response.status}`,
      body?.error?.request_id,
      body?.error?.details,
    );
  }

  return data as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

/** fetch: EventSource can't POST. */
export async function streamChat(params: {
  /** Default: session JWT. */
  apiKey?: string;
  path?: string;
  body: Record<string, unknown>;
  signal?: AbortSignal;
  onChunk: (delta: string) => void;
  onMeta: (meta: { model?: string; provider?: string; id?: string; usage?: unknown; billing?: unknown }) => void;
  onError: (error: { type: string; message: string; request_id?: string }) => void;
}): Promise<void> {
  const send = () =>
    fetch(`${API_BASE}${params.path ?? '/v1/chat/completions'}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${params.apiKey ?? tokens.access ?? ''}`,
        accept: 'text/event-stream',
      },
      body: JSON.stringify({ ...params.body, stream: true, stream_options: { include_usage: true } }),
      signal: params.signal,
    });

  let response = await send();
  // session may be stale
  if (response.status === 401 && !params.apiKey && tokens.refresh && (await refreshAccessToken())) {
    response = await send();
  }

  if (!response.ok || !response.body) {
    const body = (await response.json().catch(() => null)) as ApiErrorBody | null;
    params.onError({
      type: body?.error?.code ?? body?.error?.type ?? 'request_error',
      message: body?.error?.message ?? `Stream failed with status ${response.status}`,
      request_id: body?.error?.request_id,
    });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    buffer = buffer.replace(/\r\n/g, '\n');

    let index: number;
    while ((index = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, index);
      buffer = buffer.slice(index + 2);

      const dataLines = frame
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trimStart());
      if (dataLines.length === 0) continue; // keep-alive comment

      const payload = dataLines.join('\n');
      if (payload === '[DONE]') return;

      try {
        const parsed = JSON.parse(payload);
        if (parsed.error) {
          params.onError({ ...parsed.error, type: parsed.error.code ?? parsed.error.type });
          return;
        }
        params.onMeta({
          model: parsed.model,
          provider: parsed.provider,
          id: parsed.id,
          ...(parsed.usage ? { usage: parsed.usage, billing: parsed.billing } : {}),
        });
        const delta = parsed.choices?.[0]?.delta?.content;
        if (typeof delta === 'string') params.onChunk(delta);
      } catch {
        // skip malformed frame
      }
    }
  }
}
